import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateManualAbsenceInput,
  ManualAbsence,
  UpdateManualAbsenceInput,
} from "@msc/shared";

import { api } from "../lib/api.js";

const SCHLUESSEL = "absences-manual";

interface ListeResponse {
  items: ManualAbsence[];
}

export interface AbsenceFilter {
  segmentId?: string;
  employeeId?: string;
  from?: string;
  to?: string;
}

function suche(filter: AbsenceFilter): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v) params.set(k, v);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useManualAbsences(filter: AbsenceFilter) {
  return useQuery<ManualAbsence[]>({
    // Der Filter steckt im Schlüssel, damit ein Wechsel von Bereich oder
    // Zeitraum eine eigene Antwort bekommt — und damit das Präfix unten
    // trotzdem alle Varianten auf einmal ungültig machen kann.
    queryKey: [SCHLUESSEL, filter],
    queryFn: async () => {
      const res = await api.get<ListeResponse>(`/absences/manual${suche(filter)}`);
      return res.items;
    },
  });
}

/**
 * Nach jedem Schreiben wird über das PRÄFIX ungültig gemacht, nicht über den
 * genauen Schlüssel: Ein Eintrag für Januar verändert auch die Liste, die ein
 * anderer Filter gerade zeigt, und die Abwesenheiten stecken zusätzlich im
 * TeamDaily-Board und in den Kennzahlen.
 */
function useAufraeumen() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: [SCHLUESSEL] });
    void client.invalidateQueries({ queryKey: ["teamdaily"] });
    void client.invalidateQueries({ queryKey: ["kpi"] });
    void client.invalidateQueries({ queryKey: ["status-reports"] });
  };
}

export function useCreateManualAbsences() {
  const aufraeumen = useAufraeumen();
  return useMutation<ListeResponse, Error, CreateManualAbsenceInput>({
    mutationFn: (input) => api.post<ListeResponse>("/absences/manual", input),
    onSuccess: aufraeumen,
  });
}

export function useUpdateManualAbsence() {
  const aufraeumen = useAufraeumen();
  return useMutation<
    ManualAbsence,
    Error,
    { id: string; input: UpdateManualAbsenceInput }
  >({
    mutationFn: ({ id, input }) =>
      api.patch<ManualAbsence>(`/absences/manual/${id}`, input),
    onSuccess: aufraeumen,
  });
}

export function useDeleteManualAbsence() {
  const aufraeumen = useAufraeumen();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/absences/manual/${id}`),
    onSuccess: aufraeumen,
  });
}
